import React, { useCallback } from 'react';

import { Avatar } from '@vk-hr-tek/ui/Avatar';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { toDateDeclensionMonth } from '@vk-hr-tek/ui/format';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { FormatService } from '@vk-hr-tek/core/format';
import { Link } from '@vk-hr-tek/ui/Link';
import { useInject } from '@vk-hr-tek/core/ioc';
import { useLexicalText } from '@vk-hr-tek/ui/hooks';

import { modules } from '@app/modules';

import { PhotoParams, SearchCommentItem } from '../SearchInput.types';
import { useUrlWithPrefix } from '../../../../hooks';

interface SearchOptionCommentProps {
  option: SearchCommentItem;
  loadPhoto: (params: PhotoParams) => Promise<Blob>;
}

export const SearchOptionComment = ({
  option,
  loadPhoto,
}: SearchOptionCommentProps) => {
  const format = useInject(FormatService);
  const handleLoadPhoto = useCallback(async () => {
    return loadPhoto({
      type: 'comment',
      id: option.user.id,
    });
  }, [loadPhoto, option.user.id]);

  const urlWithPrefix = useUrlWithPrefix();
  const getLexicalText = useLexicalText();

  const href = `/${modules.news.basename}/${option.affiliation.id}#comment-${option.id}`;

  return (
    <Box width="100%">
      <Link {...urlWithPrefix(href)} variant="simple" display="block">
        <Box
          p="8"
          width="100%"
          display="flex"
          justifyContent="space-between"
          gap="12"
          cursor="pointer"
        >
          <Box
            height={40}
            width={40}
            display="flex"
            gap={12}
            position="relative"
          >
            <Avatar
              loadPhoto={handleLoadPhoto}
              children={`${option.user.second_name[0]}${option.user.first_name[0]}`}
            />
            <Box
              height={20}
              width={20}
              position="absolute"
              right={-4}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="bg.light.primary"
              radius="round"
            >
              <Icon name="comment_alt" size="16" />
            </Box>
          </Box>
          <Box flex={1} display="flex" flexDirection="column">
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" gap={10}>
                <Typography variant="caption" color="text.light.tertirary">
                  {format.toShortName(
                    option.user.first_name,
                    option.user.middle_name,
                    option.user.second_name,
                  )}
                </Typography>
                <Typography variant="caption" color="text.dark.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="text.light.tertirary">
                  Комментарий к{' '}
                  {option.affiliation.name === 'news'
                    ? 'новости'
                    : 'сообществу'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.light.tertirary">
                {toDateDeclensionMonth(option.date)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body3"
                color="text.light.primary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {getLexicalText(option.text) || (
                  <Typography variant="body3" color="text.light.tertirary">
                    *Комментарий без текста*
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Link>
    </Box>
  );
};
